import React, { useMemo, useRef, useState } from 'react';
import { uploadProductImage } from '../lib/adminApi';

function ImageUploader({
  images,
  onChange,
  variants = [],
  variantId = null,
  filterToVariant = false,
  compact = false,
  showVariantSelect = true,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragIndex, setDragIndex] = useState(null);

  const visibleImages = useMemo(() => {
    if (!filterToVariant) return images;
    if (variantId == null) {
      return images.filter(image => !image.variant_id);
    }
    return images.filter(image => image.variant_id === variantId);
  }, [images, filterToVariant, variantId]);

  const handleFiles = async fileList => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;

    setUploading(true);
    setError('');

    try {
      const uploaded = [];
      for (const file of files) {
        const url = await uploadProductImage(file);
        uploaded.push({
          id: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          image_url: url,
          is_thumbnail: images.length === 0 && uploaded.length === 0,
          variant_id: variantId || null,
          alt_text: '',
        });
      }

      const next = [...images, ...uploaded];
      if (!next.some(img => img.is_thumbnail) && next.length) {
        next[0] = { ...next[0], is_thumbnail: true };
      }
      onChange(next);
    } catch (err) {
      setError(err.message || 'Could not upload image.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const setThumbnail = imageId => {
    onChange(images.map(img => ({ ...img, is_thumbnail: img.id === imageId })));
  };

  const removeImage = imageId => {
    const next = images.filter(img => img.id !== imageId);
    if (next.length && !next.some(img => img.is_thumbnail)) {
      next[0] = { ...next[0], is_thumbnail: true };
    }
    onChange(next);
  };

  const updateImage = (imageId, patch) => {
    onChange(images.map(img => (img.id === imageId ? { ...img, ...patch } : img)));
  };

  const onDragStart = index => setDragIndex(index);

  const onDropAt = index => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      return;
    }

    if (filterToVariant) {
      const visibleIds = visibleImages.map(img => img.id);
      const fromId = visibleIds[dragIndex];
      const toId = visibleIds[index];
      const full = [...images];
      const fromIndex = full.findIndex(img => img.id === fromId);
      const toIndex = full.findIndex(img => img.id === toId);
      if (fromIndex < 0 || toIndex < 0) {
        setDragIndex(null);
        return;
      }
      const [moved] = full.splice(fromIndex, 1);
      full.splice(toIndex, 0, moved);
      onChange(full);
      setDragIndex(null);
      return;
    }

    const next = [...images];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    onChange(next);
    setDragIndex(null);
  };

  return (
    <div className={compact ? 'admin-image-uploader admin-image-uploader--compact' : 'admin-image-uploader'}>
      {error && <p className="admin-error">{error}</p>}

      <div
        className={`admin-dropzone${dragging ? ' dragging' : ''}${compact ? ' compact' : ''}`}
        onDragOver={e => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <p className="admin-dropzone-title">
          {uploading
            ? 'Uploading…'
            : compact
              ? 'Drop images here'
              : 'Drag & drop product images here'}
        </p>
        {!compact && (
          <p className="admin-dropzone-hint">Recommended size: 512 × 705 px</p>
        )}
        <button
          type="button"
          className="admin-btn admin-btn-secondary admin-btn-sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {compact ? 'Upload images' : 'Browse files'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={e => handleFiles(e.target.files)}
        />
      </div>

      <div className={compact ? 'admin-image-grid' : 'admin-image-list'}>
        {visibleImages.map((image, index) => (
          <div
            key={image.id || image.image_url}
            className={compact ? 'admin-image-tile' : 'admin-image-row'}
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={e => e.preventDefault()}
            onDrop={() => onDropAt(index)}
          >
            <img src={image.image_url} alt="" className="admin-thumb" />

            {compact ? (
              <div className="admin-image-tile-actions">
                <button
                  type="button"
                  className={`admin-btn admin-btn-sm ${
                    image.is_thumbnail ? 'admin-btn-primary' : 'admin-btn-secondary'
                  }`}
                  onClick={() => setThumbnail(image.id)}
                >
                  {image.is_thumbnail ? 'Main' : 'Set main'}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-danger admin-btn-sm"
                  onClick={() => removeImage(image.id)}
                >
                  Delete
                </button>
              </div>
            ) : (
              <>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Image {index + 1}</div>
                  <div className="admin-grid-2">
                    {showVariantSelect && variants.length > 0 && (
                      <div>
                        <label className="admin-label">Colour variant</label>
                        <select
                          className="admin-input"
                          value={image.variant_id || ''}
                          onChange={e =>
                            updateImage(image.id, { variant_id: e.target.value || null })
                          }
                        >
                          <option value="">Shared / all colours</option>
                          {variants.map(v => (
                            <option key={v.id} value={v.id}>
                              {v.name || 'Untitled colour'}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                      <button
                        type="button"
                        className={`admin-btn admin-btn-sm ${
                          image.is_thumbnail ? 'admin-btn-primary' : 'admin-btn-secondary'
                        }`}
                        onClick={() => setThumbnail(image.id)}
                      >
                        {image.is_thumbnail ? 'Main thumbnail' : 'Set as thumbnail'}
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-btn admin-btn-danger admin-btn-sm"
                  onClick={() => removeImage(image.id)}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {!visibleImages.length && (
        <p className="admin-empty" style={{ padding: compact ? 8 : 16 }}>
          {compact ? 'No images for this colour yet.' : 'No images yet.'}
        </p>
      )}
    </div>
  );
}

export default ImageUploader;
